import { blue, blueGrey, common, grey } from '@mui/material/colors';
import { alpha, createTheme } from '@mui/material/styles';
import type {} from '@mui/x-data-grid/themeAugmentation';

import { BRONZE, GOLD, MECHA_METAL, PLATINUM } from './configs/colours';
import { DARK_THEME_COLOURS, DARK_THEME_GREY, createDarkModeComponentOverrides } from './configs/darkTheme';

export const theme = createTheme({
  typography: {
    allVariants: {
      color: grey[900],
    },
    h1: {
      fontSize: '1.8rem',
      fontWeight: 'normal',
    },
    h2: {
      fontSize: '1.5rem',
      fontWeight: 'normal',
    },
    h3: {
      fontSize: '1.25rem',
      fontWeight: 'normal',
    },
    h4: {
      fontSize: '1.125rem',
      fontWeight: 'normal',
    },
    h5: {
      fontSize: '1rem',
      fontWeight: 'normal',
    },
    h6: {
      fontSize: '1rem',
      fontWeight: 'normal',
    },
  },
  palette: {
    primary: {
      light: blue[400],
      main: blue[500],
      dark: blue[600],
    },
    secondary: {
      light: blueGrey[400],
      main: blueGrey[500],
      dark: blueGrey[600],
    },
    neutral: {
      light: grey[400],
      main: grey[500],
      dark: grey[600],
      contrastText: common.black,
    },
    tertiary: {
      light: grey[300],
      main: grey[400],
      dark: grey[500],
      contrastText: common.black,
    },
    text: {
      primary: grey[900],
      secondary: grey[600],
    },
    luxPlus: {
      main: '#304DAB',
      contrastText: common.white,
      light: '#5DA4FA',
      dark: '#0F1C47',
    },
    loyaltyTiers: {
      bronze: {
        main: BRONZE[300],
        contrastText: common.black,
        light: BRONZE[100],
        dark: BRONZE[500],
        gradient: `linear-gradient(45deg, ${BRONZE[300]} 0.73%, ${BRONZE[100]} 45.77%, ${BRONZE[300]} 74.49%)`,
      },
      silver: {
        main: MECHA_METAL[300],
        contrastText: common.black,
        light: MECHA_METAL[100],
        dark: MECHA_METAL[500],
        gradient: `linear-gradient(45deg, ${MECHA_METAL[300]} 0.73%, ${MECHA_METAL[100]} 45.77%, ${MECHA_METAL[300]} 74.49%)`,
      },
      gold: {
        main: GOLD[300],
        contrastText: common.black,
        light: GOLD[100],
        dark: GOLD[500],
        gradient: `linear-gradient(45deg, ${GOLD[300]} 0.73%, ${GOLD[100]} 45.77%, ${GOLD[300]} 74.49%)`,
      },
      platinum: {
        main: PLATINUM[600],
        contrastText: common.white,
        light: PLATINUM[400],
        dark: PLATINUM[700],
        gradient: `linear-gradient(45deg, ${PLATINUM[900]} 0.73%, ${PLATINUM[600]} 45.77%, ${PLATINUM[900]} 74.49%)`,
      },
    },
  },

  components: {
    MuiButton: {
      styleOverrides: {
        text: {
          userSelect: 'text',
        },
      },
    },

    MuiInputBase: {
      defaultProps: {
        size: 'small',
      },
    },

    MuiFormControl: {
      defaultProps: {
        size: 'small',
      },
    },

    MuiSelect: {
      defaultProps: {
        size: 'small',
      },
    },

    MuiPaper: {
      defaultProps: {
        elevation: 0,
      },
    },

    MuiAutocomplete: {
      defaultProps: {
        size: 'small',
        slotProps: {
          paper: {
            elevation: 1,
          },
        },
      },
    },

    MuiDataGrid: {
      styleOverrides: {
        root: {
          border: 'none',
          fontSize: '1rem',
        },
        columnHeaders: {
          background: grey[200],
        },
        row: {
          cursor: 'pointer',
        },
        cell: {
          paddingBottom: 3,
          paddingTop: 3,
        },
      },
    },
    /*
    MuiInputLabel: {
      styleOverrides: {
        root: ({ ownerState }) => ({
          transform: ownerState.shrink ? 'translate(14px, -10px) scale(0.9)' : 'translate(14px, 10px) scale(1)',
        }),
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        inputRoot: {
          padding: '10px 14px',
        },
        input: {
          padding: '0 !important',
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        input: {
          padding: '10px 14px',
        },
        notchedOutline: {
          '& legend': {
            fontSize: '0.9em',
          },
        },
      },
    },
    */
  },
});

export const darkTheme = createTheme(theme, {
  typography: {
    allVariants: {
      color: 'inherit',
    },
  },
  palette: {
    mode: 'dark',
    primary: {
      light: blue[200],
      main: blue[300],
      dark: blue[500],
      contrastText: grey[900],
    },
    secondary: {
      light: blueGrey[200],
      main: blueGrey[300],
      dark: blueGrey[500],
      contrastText: grey[900],
    },
    neutral: {
      light: grey[700],
      main: grey[800],
      dark: grey[900],
      contrastText: grey[100],
    },
    tertiary: {
      light: grey[600],
      main: grey[700],
      dark: grey[800],
      contrastText: grey[100],
    },
    background: {
      default: DARK_THEME_COLOURS.backgroundDefault,
      paper: DARK_THEME_COLOURS.backgroundPaper,
    },
    grey: DARK_THEME_GREY,
    text: {
      primary: DARK_THEME_COLOURS.textPrimary,
      secondary: DARK_THEME_COLOURS.textSecondary,
      disabled: DARK_THEME_COLOURS.textDisabled,
    },
    divider: alpha(common.white, 0.16),
    action: {
      active: '#d7dde5',
      hover: alpha(common.white, 0.08),
      selected: alpha(blue[300], 0.16),
      disabled: alpha(common.white, 0.32),
      disabledBackground: alpha(common.white, 0.12),
    },
  },
  components: createDarkModeComponentOverrides(blue[200]),
});

export const appThemes = {
  light: theme,
  dark: darkTheme,
} as const;
